import { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useEnhancedGameSession } from '../hooks/useEnhancedGameSession'
import { VoiceChat } from '../components/VoiceChat'
import { GameSetupScreen } from '../components/GameSetupScreen'
import { GameHeader } from '../components/GameHeader'
import { GameContext } from '../components/GameContext'
import { sounds } from '../utils/sounds'
import { UserProfileManager } from '../utils/userProfile'
import { BigTwoAI } from '../utils/ai/bigtwo'
import { GameRoomSidebar } from '../components/GameRoom'
import type { Message, HistoryItem } from '../components/GameRoom/GameRoomSidebar'
import { useSidebar } from '../hooks/useSidebar'
import '../styles/GamePage.css'
import '../styles/BigTwo.css'
import '../styles/UniversalGameRoom.css'
import '../styles/mobile-fixes.css'
import '../styles/mobile-bottom-sheet.css'

// Card types and rankings
type Suit = '♠' | '♥' | '♣' | '♦'
type Rank = '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K' | 'A' | '2'

interface Card {
  suit: Suit
  rank: Rank
  id: string
}

type GamePhase = 'select' | 'setup' | 'lobby' | 'playing' | 'finished'
type GameMode = 'select' | 'local' | 'network' | 'ai'
type AIDifficulty = 'easy' | 'medium' | 'hard'

const RANK_ORDER: Rank[] = ['3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A', '2']
const SUITS: Suit[] = ['♦', '♣', '♥', '♠'] // Diamond < Club < Heart < Spade

export default function BigTwo() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const autoJoinCode = searchParams.get('join')

  // Auto-fill from profile
  const userProfile = UserProfileManager.get()

  // Game mode and phase
  const [gameMode, setGameMode] = useState<GameMode>('select')
  const [phase, setPhase] = useState<GamePhase>('select')
  const [aiDifficulty, setAiDifficulty] = useState<AIDifficulty>('medium')

  // AI instance
  const aiInstanceRef = useRef<BigTwoAI | null>(null)

  // Player info
  const [playerName, setPlayerName] = useState(userProfile?.name || '')
  const [playerNumber, setPlayerNumber] = useState<1 | 2>(1)

  // Network multiplayer
  const enhancedSession = useEnhancedGameSession({
    sessionCode: undefined, // Will be set after creation/join
    playerNumber,
    playerName,
  })

  const {
    session,
    createSession,
    joinSession,
    voice,
    
    sendMove,
    onMessage,
  } = enhancedSession

  // Game state
  const [player1Hand, setPlayer1Hand] = useState<Card[]>([])
  const [player2Hand, setPlayer2Hand] = useState<Card[]>([])
  const [selectedCards, setSelectedCards] = useState<string[]>([])
  const [lastPlay, setLastPlay] = useState<Card[]>([])
  const [currentTurn, setCurrentTurn] = useState<1 | 2>(1)
  const [winner, setWinner] = useState<1 | 2 | null>(null)
  const [passCount, setPassCount] = useState(0)

  // Sidebar state
  const [chatMessages, setChatMessages] = useState<Message[]>([])
  const [historyItems] = useState<HistoryItem[]>([])
  const sidebar = useSidebar()

  // Create deck
  const createDeck = (): Card[] => {
    const cards: Card[] = []
    for (const suit of SUITS) {
      for (const rank of RANK_ORDER) {
        cards.push({ suit, rank, id: `${suit}${rank}` })
      }
    }
    return cards.sort(() => Math.random() - 0.5)
  }

  // Deal cards
  const dealCards = () => {
    const newDeck = createDeck()
    const p1 = newDeck.slice(0, 13).sort((a, b) => compareCards(a, b))
    const p2 = newDeck.slice(13, 26).sort((a, b) => compareCards(a, b))

    setPlayer1Hand(p1)
    setPlayer2Hand(p2)
    setLastPlay([])
    setPassCount(0)

    // Player with 3 of Diamonds starts
    const hasThreeDiamonds = (hand: Card[]) => hand.some(c => c.rank === '3' && c.suit === '♦')
    setCurrentTurn(hasThreeDiamonds(p1) ? 1 : 2)
  }

  // Compare cards (for sorting)
  const compareCards = (a: Card, b: Card): number => {
    const rankDiff = RANK_ORDER.indexOf(a.rank) - RANK_ORDER.indexOf(b.rank)
    if (rankDiff !== 0) return rankDiff
    return SUITS.indexOf(a.suit) - SUITS.indexOf(b.suit)
  }

  // Check if cards form a valid play
  const isValidPlay = (cards: Card[]): { valid: boolean; type: string; rank?: number } => {
    if (cards.length === 0) return { valid: false, type: '' }

    // Single card
    if (cards.length === 1) return { valid: true, type: 'single' }

    // Pair
    if (cards.length === 2 && cards[0].rank === cards[1].rank) {
      return { valid: true, type: 'pair' }
    }

    // Triple
    if (cards.length === 3 && cards[0].rank === cards[1].rank && cards[1].rank === cards[2].rank) {
      return { valid: true, type: 'triple' }
    }

    // 5-card combos
    if (cards.length === 5) {
      const sorted = [...cards].sort((a, b) => RANK_ORDER.indexOf(a.rank) - RANK_ORDER.indexOf(b.rank))

      // Check for straight (5 consecutive ranks)
      let isStraight = true
      for (let i = 0; i < 4; i++) {
        if (RANK_ORDER.indexOf(sorted[i + 1].rank) !== RANK_ORDER.indexOf(sorted[i].rank) + 1) {
          isStraight = false
          break
        }
      }

      // Check for flush (all same suit)
      const isFlush = cards.every(card => card.suit === cards[0].suit)

      // Straight Flush (rank 5)
      if (isStraight && isFlush) {
        return { valid: true, type: 'straight-flush', rank: 5 }
      }

      // Check for Four of a Kind (rank 4)
      const rankCounts: { [key: string]: number } = {}
      cards.forEach(card => {
        rankCounts[card.rank] = (rankCounts[card.rank] || 0) + 1
      })
      const counts = Object.values(rankCounts)
      if (counts.includes(4)) {
        return { valid: true, type: 'four-of-a-kind', rank: 4 }
      }

      // Check for Full House (rank 3)
      if (counts.includes(3) && counts.includes(2)) {
        return { valid: true, type: 'full-house', rank: 3 }
      }

      // Flush (rank 2)
      if (isFlush) {
        return { valid: true, type: 'flush', rank: 2 }
      }

      // Straight (rank 1)
      if (isStraight) {
        return { valid: true, type: 'straight', rank: 1 }
      }
    }

    return { valid: false, type: '' }
  }

  // Check if play beats last play
  const beatsLastPlay = (cards: Card[]): boolean => {
    // Free play if table is clear OR if opponent just passed
    if (lastPlay.length === 0 || passCount === 1) return true

    const newPlay = isValidPlay(cards)
    const oldPlay = isValidPlay(lastPlay)

    if (!newPlay.valid) return false
    if (cards.length !== lastPlay.length) return false

    // For 5-card combos, use combo ranking system
    if (cards.length === 5 && newPlay.rank !== undefined && oldPlay.rank !== undefined) {
      // Higher rank combo always wins (Straight < Flush < Full House < Four of a Kind < Straight Flush)
      if (newPlay.rank !== oldPlay.rank) {
        return newPlay.rank > oldPlay.rank
      }

      // Same rank combo - compare by card values
      if (newPlay.type === 'full-house' && oldPlay.type === 'full-house') {
        // Full House: compare triple rank
        const newRanks = cards.map(c => c.rank)
        const oldRanks = lastPlay.map(c => c.rank)
        const newTriple = newRanks.find(r => newRanks.filter(x => x === r).length === 3)!
        const oldTriple = oldRanks.find(r => oldRanks.filter(x => x === r).length === 3)!
        return RANK_ORDER.indexOf(newTriple) > RANK_ORDER.indexOf(oldTriple)
      }

      if (newPlay.type === 'four-of-a-kind' && oldPlay.type === 'four-of-a-kind') {
        // Four of a Kind: compare the four cards rank
        const newRanks = cards.map(c => c.rank)
        const oldRanks = lastPlay.map(c => c.rank)
        const newFour = newRanks.find(r => newRanks.filter(x => x === r).length === 4)!
        const oldFour = oldRanks.find(r => oldRanks.filter(x => x === r).length === 4)!
        return RANK_ORDER.indexOf(newFour) > RANK_ORDER.indexOf(oldFour)
      }

      // Straight, Flush, Straight Flush: compare highest card
      const newSorted = [...cards].sort((a, b) => compareCards(b, a))
      const oldSorted = [...lastPlay].sort((a, b) => compareCards(b, a))
      return compareCards(newSorted[0], oldSorted[0]) > 0
    }

    // For non-5-card combos, must match type exactly
    if (newPlay.type !== oldPlay.type) return false

    // Compare highest card
    const newHighest = [...cards].sort((a, b) => compareCards(b, a))[0]
    const oldHighest = [...lastPlay].sort((a, b) => compareCards(b, a))[0]

    return compareCards(newHighest, oldHighest) > 0
  }

  // Play selected cards
  const playCards = () => {
    const currentHand = currentTurn === 1 ? player1Hand : player2Hand
    const cards = selectedCards.map(id => currentHand.find(c => c.id === id)!).filter(Boolean)

    // Must include 3 of Diamonds on first move
    if (lastPlay.length === 0 && passCount === 0) {
      const has3D = cards.some(c => c.rank === '3' && c.suit === '♦')
      if (!has3D) {
        alert('First play must include 3 of Diamonds!')
        return
      }
    }

    const playCheck = isValidPlay(cards)
    if (!playCheck.valid) {
      alert('Invalid card combination!')
      return
    }

    if (!beatsLastPlay(cards)) {
      alert('Your play must beat the last play!')
      return
    }

    // Remove cards from hand
    const newHand = currentHand.filter(c => !selectedCards.includes(c.id))

    if (currentTurn === 1) {
      setPlayer1Hand(newHand)
    } else {
      setPlayer2Hand(newHand)
    }

    setLastPlay(cards)
    setSelectedCards([])
    setPassCount(0)
    sounds.click()

    // Check win
    if (newHand.length === 0) {
      setWinner(currentTurn)
      setPhase('finished')
      sounds.success()

      // Send final state
      if (gameMode === 'network') {
        sendMove({ type: 'game_state', state: {
          player1Hand: currentTurn === 1 ? newHand : player1Hand,
          player2Hand: currentTurn === 2 ? newHand : player2Hand,
          lastPlay: cards,
          currentTurn,
          passCount: 0,
          winner: currentTurn,
        }})
      }
      return
    }

    const nextTurn = currentTurn === 1 ? 2 : 1
    setCurrentTurn(nextTurn)
    sounds.turnChange()

    // Send state update
    if (gameMode === 'network') {
      sendMove({ type: 'game_state', state: {
        player1Hand: currentTurn === 1 ? newHand : player1Hand,
        player2Hand: currentTurn === 2 ? newHand : player2Hand,
        lastPlay: cards,
        currentTurn: nextTurn,
        passCount: 0,
      }})
    }
  }

  // Pass turn
  const pass = () => {
    if (lastPlay.length === 0) {
      alert('Cannot pass on first turn!')
      return
    }

    const newPassCount = passCount + 1
    setPassCount(newPassCount)

    // If both players pass, clear the table
    const newLastPlay = newPassCount >= 2 ? [] : lastPlay
    const finalPassCount = newPassCount >= 2 ? 0 : newPassCount

    if (newPassCount >= 2) {
      setLastPlay([])
      setPassCount(0)
    }

    const nextTurn = currentTurn === 1 ? 2 : 1
    setCurrentTurn(nextTurn)
    setSelectedCards([])
    sounds.click()

    // Send state update
    if (gameMode === 'network') {
      sendMove({ type: 'game_state', state: {
        player1Hand,
        player2Hand,
        lastPlay: newLastPlay,
        currentTurn: nextTurn,
        passCount: finalPassCount,
      }})
    }
  }

  // AI turn logic
  useEffect(() => {
    if (gameMode !== 'ai') return
    if (phase !== 'playing') return
    if (currentTurn !== 2) return // AI is player 2
    if (!aiInstanceRef.current) return
    if (winner) return

    // Update AI's hand and last play
    aiInstanceRef.current.setHand(player2Hand)
    aiInstanceRef.current.setLastPlay(lastPlay)

    // AI makes decision after delay
    const delay = aiDifficulty === 'easy' ? 1500 : aiDifficulty === 'medium' ? 1200 : 800
    const timer = setTimeout(() => {
      if (!aiInstanceRef.current) return

      const decision = aiInstanceRef.current.play()

      if (decision === 'pass') {
        // AI passes
        const newPassCount = passCount + 1

        if (newPassCount >= 2) {
          setLastPlay([])
          setPassCount(0)
          aiInstanceRef.current.resetPassCount()
        } else {
          setPassCount(newPassCount)
        }

        setCurrentTurn(1)
        sounds.click()
      } else {
        // AI plays cards
        const cards = decision
        aiInstanceRef.current.removeCards(cards)

        const newHand = player2Hand.filter(c => !cards.some(ac => ac.id === c.id))
        setPlayer2Hand(newHand)
        setLastPlay(cards)
        setPassCount(0)
        aiInstanceRef.current.resetPassCount()
        sounds.click()

        // Check win
        if (newHand.length === 0) {
          setWinner(2)
          setPhase('finished')
          sounds.success()
          return
        }

        setCurrentTurn(1)
        sounds.turnChange()
      }
    }, delay)

    return () => clearTimeout(timer)
  }, [gameMode, phase, currentTurn, player2Hand, lastPlay, passCount, aiDifficulty, winner])

  // Handler for sidebar message sending
  const handleSidebarMessage = async (message: string) => {
    if (gameMode === 'network') {
      await sendMove({ type: 'chat', message })
      // Add to local messages
      const newMessage: Message = {
        sender: 'you',
        content: message,
        timestamp: Date.now()
      }
      setChatMessages(prev => [...prev, newMessage])
    }
  }

  // Sync game state (network mode)
  useEffect(() => {
    if (gameMode !== 'network') return

    const unsubscribe = onMessage('game_state', (data) => {
      const state = data.state
      if (state.player1Hand) setPlayer1Hand(state.player1Hand)
      if (state.player2Hand) setPlayer2Hand(state.player2Hand)
      if (state.lastPlay) setLastPlay(state.lastPlay)
      if (state.currentTurn) setCurrentTurn(state.currentTurn)
      if (state.passCount !== undefined) setPassCount(state.passCount)
      if (state.winner !== undefined) {
        setWinner(state.winner)
        if (state.winner) setPhase('finished')
      }
    })

    const unsubscribeChat = onMessage('chat', (data) => {
      const newMessage: Message = {
        sender: data.playerNumber === playerNumber ? 'you' : 'opponent',
        content: data.message,
        timestamp: Date.now()
      }
      setChatMessages(prev => [...prev, newMessage])
    })

    return () => {
      unsubscribe()
      unsubscribeChat()
    }
  }, [gameMode, onMessage, playerNumber])

  // Check if player joined
  useEffect(() => {
    if (gameMode === 'network' && phase !== 'finished' && session?.player2_name && phase !== 'playing') {
      setPhase('playing')
      sounds.playerJoined()
    }
  }, [session?.player2_name, phase, gameMode])

  // Toggle card selection
  const toggleCard = (cardId: string) => {
    if (selectedCards.includes(cardId)) {
      setSelectedCards(selectedCards.filter(id => id !== cardId))
    } else {
      setSelectedCards([...selectedCards, cardId])
    }
    sounds.click()
  }

  // Mode selection (arcade-style)
  if (phase === 'select') {
    return (
      <GameSetupScreen
        gameTitle="大老二 (Big Two)"
        gameIcon="🎴"
        gameBanner="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        supportedModes={['local', 'network', 'ai']}
        onModeSelect={(mode) => {
          setGameMode(mode)
          setPhase('setup')
        }}
        onBack={() => navigate('/')}
      />
    )
  }

  // Setup phase
  // Setup screen (arcade-style)
  if (phase === 'setup') {
    return (
      <div className="bigtwo-container">
        <div className="arcade-setup-screen">
          <div className="arcade-setup-card">
            <h2>🎴 {gameMode === 'ai' ? 'AI Game' : gameMode === 'network' ? 'Online Game' : 'Local Game'}</h2>

            <div className="setup-form">
              <div className="input-group">
                <label htmlFor="player-name">Your Name</label>
                <input
                  type="text"
                  id="player-name"
                  className="input"
                  placeholder="Enter your name"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  maxLength={20}
                  autoFocus
                />
              </div>

              {gameMode === 'ai' && (
                <div className="input-group">
                  <label>AI Difficulty</label>
                  <div className="difficulty-selector">
                    <button
                      className={`difficulty-btn ${aiDifficulty === 'easy' ? 'active' : ''}`}
                      onClick={() => setAiDifficulty('easy')}
                    >
                      😊 Easy
                    </button>
                    <button
                      className={`difficulty-btn ${aiDifficulty === 'medium' ? 'active' : ''}`}
                      onClick={() => setAiDifficulty('medium')}
                    >
                      😐 Medium
                    </button>
                    <button
                      className={`difficulty-btn ${aiDifficulty === 'hard' ? 'active' : ''}`}
                      onClick={() => setAiDifficulty('hard')}
                    >
                      😤 Hard
                    </button>
                  </div>
                </div>
              )}

              <div className="setup-actions">
                {gameMode === 'ai' && (
                  <button
                    className="btn btn-primary btn-lg"
                    onClick={() => {
                      if (!playerName) return
                      aiInstanceRef.current = new BigTwoAI()
                      dealCards()
                      setPhase('playing')
                    }}
                    disabled={!playerName}
                  >
                    🎮 Start Game
                  </button>
                )}

                {gameMode === 'network' && (
                  <>
                    <button
                      className="btn btn-primary btn-lg"
                      onClick={async () => {
                        if (!playerName) return
                        await createSession('bigtwo', playerName)
                        setPlayerNumber(1)
                        const newDeck = createDeck()
                        const p1 = newDeck.slice(0, 13).sort((a, b) => compareCards(a, b))
                        const p2 = newDeck.slice(13, 26).sort((a, b) => compareCards(a, b))
                        const hasThreeDiamonds = (hand: Card[]) => hand.some(c => c.rank === '3' && c.suit === '♦')
                        const firstTurn = hasThreeDiamonds(p1) ? 1 : 2

                        setPlayer1Hand(p1)
                        setPlayer2Hand(p2)
                        setLastPlay([])
                        setPassCount(0)
                        setCurrentTurn(firstTurn as 1 | 2)

                        sendMove({ type: 'game_state', state: {
                          player1Hand: p1,
                          player2Hand: p2,
                          lastPlay: [],
                          currentTurn: firstTurn,
                          passCount: 0,
                        }})

                        setPhase('lobby')
                      }}
                      disabled={!playerName}
                    >
                      ➕ Create Game
                    </button>

                    <div className="divider-text">or</div>

                    <div className="join-section">
                      <input
                        type="text"
                        id="join-code"
                        className="input"
                        placeholder="Enter 6-char code"
                        defaultValue={autoJoinCode || ''}
                        maxLength={6}
                        style={{ textTransform: 'uppercase' }}
                      />
                      <button
                        className="btn btn-secondary"
                        onClick={async () => {
                          const code = (document.getElementById('join-code') as HTMLInputElement).value
                          if (!playerName || !code) return
                          await joinSession(code, playerName)
                          setPlayerNumber(2)
                          setPhase('playing')
                        }}
                        disabled={!playerName}
                      >
                        Join
                      </button>
                    </div>
                  </>
                )}

                {gameMode === 'local' && (
                  <button
                    className="btn btn-primary btn-lg"
                    onClick={() => {
                      if (!playerName) return
                      dealCards()
                      setPhase('playing')
                    }}
                    disabled={!playerName}
                  >
                    🎮 Start Local Game
                  </button>
                )}

                <button className="btn btn-secondary" onClick={() => setPhase('select')}>
                  ← Back
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Lobby - waiting for player 2
  // Lobby - arcade-style waiting screen
  if (phase === 'lobby') {
    const joinUrl = `${window.location.origin}/bigtwo?join=${session?.session_code}`

    return (
      <div className="bigtwo-container">
        {/* Standardized Header */}
        <GameHeader
          gameTitle="大老二 (Big Two)"
          gameIcon="🎴"
          players={[
            { number: 1, name: session?.player1_name || playerName, isConnected: true, avatar: '🎴' },
            { number: 2, name: 'Waiting...', isConnected: false, avatar: '?' }
          ]}
          sessionCode={session?.session_code}
        />

        {/* Arcade-style lobby screen */}
        <div className="arcade-lobby-screen">
          <div className="arcade-lobby-content">
            <div className="arcade-lobby-banner">
              <h2>Waiting for Player 2...</h2>
              <p className="session-code-display">
                Share code: <strong className="session-code-big">{session?.session_code}</strong>
              </p>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  navigator.clipboard.writeText(joinUrl)
                  sounds.click()
                }}
              >
                📋 Copy Join URL
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setPhase('setup')
                }}
                style={{ marginTop: 'var(--space-md)' }}
              >
                ← Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Main game (only render if playing or finished)
  if (phase !== 'playing' && phase !== 'finished') return null

  const currentHand = gameMode === 'ai'
    ? (currentTurn === 1 ? player1Hand : []) // Player 1's hand in AI mode
    : (currentTurn === playerNumber ? (playerNumber === 1 ? player1Hand : player2Hand) : [])
  const isMyTurn = (gameMode === 'ai' && currentTurn === 1) || (gameMode !== 'ai' && (currentTurn === playerNumber || gameMode === 'local'))

  const player1Name = gameMode === 'ai' ? playerName : gameMode === 'network' ? session?.player1_name || playerName : 'Player 1'
  const player2Name = gameMode === 'ai' ? 'AI' : gameMode === 'network' ? session?.player2_name || 'Player 2' : 'Player 2'

  return (
    <div className="bigtwo-container universal-game-room">
      {/* Standardized Game Header */}
      <GameHeader
        gameTitle="大老二 (Big Two)"
        gameIcon="🎴"
        players={
          gameMode === 'network'
            ? [
                { number: 1, name: player1Name, isConnected: true, avatar: '🎴' },
                { number: 2, name: player2Name, isConnected: !!session?.player2_name, avatar: '🃏' }
              ]
            : gameMode === 'ai'
            ? [
                { number: 1, name: playerName, isConnected: true, avatar: '🎴' },
                { number: 2, name: `AI (${aiDifficulty})`, isConnected: true, avatar: '🤖' }
              ]
            : [
                { number: 1, name: 'Player 1', isConnected: true, avatar: '🎴' },
                { number: 2, name: 'Player 2', isConnected: true, avatar: '🃏' }
              ]
        }
        sessionCode={gameMode === 'network' ? session?.session_code : undefined}
      />

      {/* Game Context - Turn & Cards Info */}
      <GameContext
        turn={`Player ${currentTurn}`}
        message={
          gameMode === 'ai' && currentTurn === 2
            ? '🤔 AI is thinking...'
            : isMyTurn
            ? 'Your turn'
            : voice.isConnected
            ? '🟢 Real-time'
            : undefined
        }
        urgency={isMyTurn ? 'attention' : 'normal'}
      />

      {/* Voice Chat Component (network mode only) */}
      {gameMode === 'network' && (
        <VoiceChat
          connectionStatus={voice.connectionStatus}
          voiceStatus={{ localMuted: voice.isMuted, remoteMuted: false, localConnected: voice.isConnected, remoteConnected: voice.isConnected }}
          playerName={playerName}
          onConnect={voice.connect}
          onToggleMute={voice.toggleMute}
        />
      )}

      {phase === 'finished' && (
        <div className="winner-overlay">
          <h2>🎉 {
            gameMode === 'ai'
              ? (winner === 1 ? 'You Win!' : 'AI Wins!')
              : winner === playerNumber ? 'You Win!' : gameMode === 'local' ? `Player ${winner} Wins!` : 'Opponent Wins!'
          }</h2>
          <button onClick={() => {
            dealCards();
            setPhase('playing');
            setWinner(null);
            // Track win if player won
            if (gameMode === 'ai' && winner === 1) {
              UserProfileManager.incrementWins()
            }
          }}>
            Play Again
          </button>
        </div>
      )}

      <div className="game-room-content">
        <div className="game-board game-board-area">
        {/* Opponent/Player 2 hand count */}
        <div className="opponent-area">
          <div>
            <h3>
              {gameMode === 'ai'
                ? `🤖 AI (${aiDifficulty.charAt(0).toUpperCase() + aiDifficulty.slice(1)})`
                : gameMode === 'network'
                ? `👤 ${session?.player2_name || 'Opponent'}`
                : '👤 Player 2'}
            </h3>
            {gameMode === 'ai' && currentTurn === 2 && (
              <div className="ai-thinking">
                🤔 AI is thinking...
              </div>
            )}
          </div>
          <div className="card-count">
            🃏 {player2Hand.length}
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', marginLeft: 'var(--space-xs)' }}>
              card{player2Hand.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Last play area */}
        <div className="play-area">
          <h3>
            {lastPlay.length > 0 ? '🎴 Last Play' : '🎴 Table'}
            {passCount > 0 && ` • ${passCount} pass${passCount > 1 ? 'es' : ''}`}
          </h3>
          <div className="last-play">
            {lastPlay.length > 0 ? (
              lastPlay.map(card => (
                <div
                  key={card.id}
                  className={`card ${card.suit === '♥' || card.suit === '♦' ? 'red' : ''}`}
                  aria-label={`${card.rank} of ${card.suit}`}
                >
                  <span className="rank">{card.rank}</span>
                  <span className="suit">{card.suit}</span>
                </div>
              ))
            ) : (
              <p>Waiting for first play...</p>
            )}
          </div>
        </div>

        {/* Player hand */}
        <div className="player-area">
          <h3 className={isMyTurn ? 'your-turn' : ''}>
            {gameMode === 'ai' ? (currentTurn === 1 ? '🎴 Your Hand' : '🤖 AI Turn...') : gameMode === 'network' ? '🎴 Your Hand' : `🎴 Player ${currentTurn}`}
            {!isMyTurn && gameMode !== 'ai' && ' (Waiting...)'}
          </h3>
          <div className="hand">
            {currentHand.map((card, index) => {
              const totalCards = currentHand.length
              const arcSpread = 120 // degrees
              const startAngle = -arcSpread / 2
              const angleStep = totalCards > 1 ? arcSpread / (totalCards - 1) : 0
              const rotation = startAngle + (angleStep * index)
              const selectIndex = selectedCards.indexOf(card.id) + 1

              return (
                <div
                  key={card.id}
                  className={`card ${selectedCards.includes(card.id) ? 'selected' : ''} ${
                    card.suit === '♥' || card.suit === '♦' ? 'red' : ''
                  } ${!isMyTurn ? 'disabled' : ''}`}
                  style={{
                    '--card-rotation': `${rotation}deg`,
                    transform: `translateX(calc(var(--card-width-desktop) / -2)) rotateZ(${rotation}deg)`,
                    zIndex: selectedCards.includes(card.id) ? 101 : 50 + index,
                  } as React.CSSProperties}
                  data-select-index={selectIndex > 0 ? selectIndex : undefined}
                  onClick={() => isMyTurn && toggleCard(card.id)}
                  aria-label={`${card.rank} of ${card.suit}`}
                  role="button"
                  tabIndex={isMyTurn ? 0 : -1}
                >
                  <span className="rank">{card.rank}</span>
                  <span className="suit">{card.suit}</span>
                </div>
              )
            })}
          </div>

          {isMyTurn && (
            <div className="actions">
              <button
                onClick={playCards}
                disabled={selectedCards.length === 0}
                aria-label={`Play ${selectedCards.length} selected card${selectedCards.length !== 1 ? 's' : ''}`}
              >
                {selectedCards.length > 0 ? `Play Cards (${selectedCards.length})` : 'Play Cards'}
              </button>
              <button
                onClick={pass}
                disabled={lastPlay.length === 0}
                aria-label="Pass your turn"
              >
                Pass
              </button>
            </div>
          )}
        </div>

        <div className="game-rules">
          <details>
            <summary>📖 How to Play</summary>
            <ul>
              <li><strong>Starting:</strong> First player must play 3 of Diamonds</li>
              <li><strong>Goal:</strong> First to empty their hand wins!</li>
              <li><strong>Card Rankings:</strong> 3 (low) &lt; 4 &lt; 5 &lt; ... &lt; A &lt; 2 (high)</li>
              <li><strong>Suit Rankings:</strong> ♦ (low) &lt; ♣ &lt; ♥ &lt; ♠ (high)</li>
              <li><strong>Valid Combos:</strong>
                <ul style={{ marginTop: '8px', fontSize: '0.9em' }}>
                  <li>Single, Pair, Triple</li>
                  <li>Straight (5 consecutive cards)</li>
                  <li>Flush (5 cards, same suit)</li>
                  <li>Full House (triple + pair)</li>
                  <li>Four of a Kind (4 cards + 1)</li>
                  <li>Straight Flush (5 consecutive, same suit)</li>
                </ul>
              </li>
              <li><strong>5-Card Combo Power:</strong> Straight &lt; Flush &lt; Full House &lt; Four of a Kind &lt; Straight Flush</li>
            </ul>
          </details>
        </div>
      </div>
      </div>

      {/* Universal Sidebar with bottom-sheet mode on mobile */}
      <GameRoomSidebar
        mode="network"
        gameState={{
          turn: `Player ${currentTurn}'s turn`,
          phase: winner ? 'Game Over' : 'Playing',
          score: {
            'Player 1': `${player1Hand.length} cards`,
            'Player 2': `${player2Hand.length} cards`,
          }
        }}
        messages={chatMessages}
        historyItems={historyItems}
        onSendMessage={handleSidebarMessage}
        collapsed={sidebar.collapsed}
        onToggleCollapse={sidebar.toggle}
        position={sidebar.position}
        quickReactions={true}
      />
    </div>
  )
}
